import { useEffect, useMemo, useState } from 'react';
import { SearchableSelect, useToast } from '../../../ui';
import { fetchAhrefsProjects, fetchLinkedProjects, looksLike, projectHost, setAhrefsProject, type AhrefsProject } from '../../../../lib/ahrefsProjects';

interface AhrefsProjectPickerProps {
  clientId: string;
  clientName: string;
  projectId: number | null;
  onChange: (projectId: number | null) => void;
  size?: 'sm' | 'md';
  style?: React.CSSProperties;
}

/**
 * Which Ahrefs project is this client's site. Projects that look like the client sort first; a project
 * already linked to another client says so, but can still be picked (a site can belong to two clients).
 */
export default function AhrefsProjectPicker({ clientId, clientName, projectId, onChange, size, style }: AhrefsProjectPickerProps) {
  const toast = useToast();
  const [projects, setProjects] = useState<AhrefsProject[] | null>(null);
  const [linked, setLinked] = useState<Map<number, { id: string; name: string }>>(new Map());
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    Promise.all([fetchAhrefsProjects(), fetchLinkedProjects()])
      .then(([list, links]) => {
        if (!live) return;
        setProjects(list);
        setLinked(links);
      })
      .catch((err) => {
        console.error('Error loading Ahrefs projects:', err);
        if (live) setLoadError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      live = false;
    };
  }, []);

  const options = useMemo(() => {
    const list = (projects ?? []).map((p) => {
      const other = linked.get(p.id);
      const suggested = looksLike(p, clientName);
      const hint = other && other.id !== clientId ? `Linked to ${other.name}` : suggested && p.id !== projectId ? 'Looks like a match' : undefined;
      return { value: String(p.id), label: projectHost(p.url) || p.name || String(p.id), hint, suggested };
    });
    list.sort((a, b) => Number(b.suggested) - Number(a.suggested) || a.label.localeCompare(b.label));
    // A saved id the account no longer has (project deleted, access removed) still shows as the value.
    if (projectId != null && projects && !list.some((o) => o.value === String(projectId))) {
      list.unshift({ value: String(projectId), label: `Project ${projectId}`, hint: 'Not on the Ahrefs account', suggested: false });
    }
    return list.map(({ value, label, hint }) => ({ value, label, hint }));
  }, [projects, linked, clientId, clientName, projectId]);

  const pick = async (value: string | null) => {
    const next = value == null ? null : Number(value);
    if (next === projectId) return;
    try {
      await setAhrefsProject(clientId, next);
      onChange(next);
      toast.success(
        next == null ? 'Ahrefs project unlinked' : `Linked to ${options.find((o) => o.value === value)?.label ?? `project ${next}`}`,
        next == null ? undefined : 'The next nightly sync pulls its pages.',
      );
    } catch (err) {
      console.error('Error saving Ahrefs project:', err);
      toast.error('Couldn’t save the Ahrefs project', err instanceof Error ? err.message : undefined);
    }
  };

  if (loadError) {
    return (
      <span style={{ fontSize: 'var(--text-xs)', color: 'var(--status-warn)', ...style }} title={loadError}>
        Couldn’t load Ahrefs projects
      </span>
    );
  }

  return (
    <SearchableSelect
      options={options}
      value={projectId == null ? null : String(projectId)}
      onChange={pick}
      allowClear={projectId != null}
      clearLabel="No Ahrefs project"
      placeholder={projects ? 'Pick Ahrefs project…' : 'Loading Ahrefs…'}
      searchPlaceholder="Search projects..."
      size={size}
      menuMinWidth={340}
      style={style}
    />
  );
}
